import * as styles from "./CollapsibleTrigger.css";

import { type JSX, useEffect, useState } from "react";

import { Collapsible as BaseCollapsible } from "@base-ui/react/collapsible";
import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { tagNameFor } from "@frontend/bridge/tagName";

import {
  CollapsibleContext,
  makeCollapsibleContext,
} from "./Collapsible.context";
import {
  type CollapsibleTriggerDataProps,
  collapsibleTriggerDataSchema,
} from "./Collapsible.schema";
import type {
  CollapsibleTriggerSize,
  CollapsibleTriggerVariant,
} from "./Collapsible.types";
import { CollapsibleTrigger } from "./CollapsibleTrigger";
import {
  COLLAPSIBLE_TAG_NAME,
  COLLAPSIBLE_TRIGGER_TAG_NAME,
} from "./constants";

export interface CollapsibleHandle {
  open: () => void;
  close: () => void;
  toggle: () => void;
}

type CollapsibleTriggerComponentProps = CollapsibleTriggerDataProps & {
  size: CollapsibleTriggerSize;
  variant: CollapsibleTriggerVariant;
  initialOpen: boolean;
  onToggle: () => void;
  subscribe: (onChange: (open: boolean) => void) => () => void;
};

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [COLLAPSIBLE_TRIGGER_TAG_NAME]: {
      props: CollapsibleTriggerComponentProps;
      handle: Record<string, never>;
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-collapsible-trigger": HTMLElement;
  }
}

const RWCCollapsibleTrigger = rwc(COLLAPSIBLE_TRIGGER_TAG_NAME, {
  component: RWCCollapsibleTriggerComponent,
  dataSchema: collapsibleTriggerDataSchema,
  usesSlots: true,
  readSlotted: readCollapsibleTriggerSlotted,
});

export function readCollapsibleTriggerSlotted(
  el: HTMLElement,
): CollapsibleTriggerComponentProps {
  el.textContent = el.getAttribute("label") ?? el.textContent?.trim() ?? "";

  const parent = el.closest<HTMLElement & Partial<CollapsibleHandle>>(
    tagNameFor(COLLAPSIBLE_TAG_NAME),
  );

  const size =
    (parent?.getAttribute("size") as CollapsibleTriggerSize) ?? undefined;

  const variant =
    (parent?.getAttribute("variant") as CollapsibleTriggerVariant) ?? undefined;

  return {
    size: size,
    variant: variant,
    initialOpen:
      parent?.hasAttribute("open") ||
      parent?.hasAttribute("default-open") ||
      false,
    onToggle: () => parent?.toggle?.(),
    subscribe: (onChange) => {
      if (!parent) {
        return () => {};
      }

      onChange(parent.hasAttribute("open"));

      const handleChange = (event: Event) => {
        onChange((event as CustomEvent<{ open: boolean }>).detail.open);
      };

      parent.addEventListener("collapsible:change", handleChange);

      return () =>
        parent.removeEventListener("collapsible:change", handleChange);
    },
  };
}

function RWCCollapsibleTriggerComponent({
  size,
  variant,
  initialOpen,
  onToggle,
  subscribe,
}: CollapsibleTriggerComponentProps): JSX.Element {
  const [open, setOpen] = useState(initialOpen);

  useEffect(() => subscribe?.(setOpen), [subscribe]);

  return (
    <BaseCollapsible.Root
      open={open}
      onOpenChange={() => onToggle()}
      render={<span className={styles.root} />}
    >
      <CollapsibleContext.Provider
        value={makeCollapsibleContext({ size, variant })}
      >
        <CollapsibleTrigger>
          <slot />
        </CollapsibleTrigger>
      </CollapsibleContext.Provider>
    </BaseCollapsible.Root>
  );
}

componentManager.register(RWCCollapsibleTrigger);
